/* eslint-disable @typescript-eslint/no-explicit-any */
import { HttpClient } from '@angular/common/http';
import { effect, inject, Injectable, OnDestroy } from '@angular/core';
import type { Session, TopicSpecification, ValueStream } from 'diffusion';
import { BehaviorSubject, interval, merge, Observable, Subject, Subscription, timer } from 'rxjs';
import { debounceTime, map, take } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { AbstractDiffusionSessionService } from './diffusion';
import { AuthStore } from '../stores/auth.store';

export class Quote {
  bid: number | undefined;
  ask: number | undefined;
  isin: string | undefined;
  time: string | undefined;
  bidv: number | undefined;
  askv: number | undefined;
}

const pollingFallbackInterval = 15000;

// Service to provide live quotes for given isins, either via Diffusion push or via fallback polling.
@Injectable({
  providedIn: 'root',
})
export class PushQuoteService implements OnDestroy {
  private readonly authStore = inject(AuthStore);
  private readonly diffusionSessionService = inject(AbstractDiffusionSessionService);
  private readonly httpClient = inject(HttpClient);

  private readonly deleteSubjectWithoutSubscriptAfter = 30; // seconds
  private consecutivePollingFailures = 0;
  private readonly maxPollingFailures = 3;

  // For book keeping of registered quote subjects
  private isins: Record<
    string,
    {
      quoteSubject: Subject<Quote>;
      noObserversPresentTimestamp?: number;
    }
  > = {};

  // To task new un/subscriptions for isins
  private readonly isinsToSubscribe = new Subject<string>();
  private readonly isinsToUnsubscribe = new Subject<string>();

  private valStream: ValueStream | undefined;

  private subscribeToDiffusionTaskSubscription: Subscription | undefined;
  private unsubscribeFromDiffusionTaskSubscription: Subscription | undefined;
  private subscribeToPollingTaskSubscription: Subscription | undefined;

  private readonly pollingTimer: Observable<number>;
  private pollingTimerSubscription: Subscription | undefined;

  private readonly observablesToUnsubcribe: Subscription[] = [];

  private isLoggedIn: boolean | undefined;

  private readonly initialPollingTrigger$ = new Subject<void>();
  private activeApiHost = environment.apihost;

  // HELPERS - convert values into usable Quote objects
  private static createQuoteFromJsonValue(value: any, isin: string): Quote {
    const q: Quote = new Quote();
    if (value.a !== 'N/A') {
      q.ask = value.a;
    }
    if (value.b !== 'N/A') {
      q.bid = value.b;
    }
    if (value.av !== 'N/A') {
      q.askv = value.av;
    }
    if (value.bv !== 'N/A') {
      q.bidv = value.bv;
    }
    q.time = new Date(value.t).toISOString();
    q.isin = isin;
    return q;
  }

  private static createQuoteFromDiffusionValue(value: any, isin: string): Quote {
    return PushQuoteService.createQuoteFromJsonValue(value.get(), isin);
  }

  // HELPERS - Buffering logic depending on the number of isins subscribed to
  public static createQuoteBufferFn(isinsAmount: number, extreme?: boolean): Observable<number> {
    // Construction of the buffer function:
    // For an initial period, buffering should only be very brief (for the duration of 1x bufferDuration),
    // so that the first prices are visible very quickly.
    // After that, buffering should always be for bufferDuration.
    const bufferDuration = PushQuoteService.calcBufferTime(isinsAmount, extreme);
    const initialShortTimeBufferingDuration = 250;
    const initialShortTimeBufferingFlushBufferCount = Math.max(
      bufferDuration / initialShortTimeBufferingDuration,
      1,
    );
    const initialShortTimeBufferingInterval = interval(initialShortTimeBufferingDuration).pipe(
      take(initialShortTimeBufferingFlushBufferCount),
    );
    const flushBufferInterval = interval(bufferDuration);
    return merge(initialShortTimeBufferingInterval, flushBufferInterval);
  }

  public static calcBufferTime(count: number, extreme?: boolean): number {
    if (extreme) {
      if (count < 101) {
        return 250;
      } else if (count < 251) {
        return 4000;
      } else if (count < 501) {
        return 6000;
      } else {
        return 7500;
      }
    } else if (count < 101) {
      return 250;
    } else if (count < 251) {
      return 1000;
    } else if (count < 501) {
      return 2500;
    } else {
      return 5000;
    }
  }

  // CONSTRUCTOR & LIFECYCLE
  constructor() {
    this.pollingTimer = timer(0, pollingFallbackInterval);
    this.observablesToUnsubcribe.push(
      this.handleDiffusionSession(),
      this.handleQuotePolling(),
      this.handlePushUnsubscribe(),
    );

    this.handleAuthState();
  }

  ngOnDestroy(): void {
    this.observablesToUnsubcribe.forEach((elem) => elem.unsubscribe());
  }

  private handleAuthState(): void {
    effect(() => {
      const token = this.authStore.token();
      const loggedIn = !!token;

      this.isLoggedIn = loggedIn;

      if (!loggedIn) {
        // stop polling in case we were
        this.pollingTimerSubscription?.unsubscribe();
        this.subscribeToPollingTaskSubscription?.unsubscribe();

        this.isins = {};
        this.activeApiHost = environment.apihost;
      } else if (!this.pollingTimerSubscription && !this.subscribeToDiffusionTaskSubscription) {
        // Token arrived after diffusionSession$ already emitted null — polling setup in
        // handleDiffusionSession only runs on emission, so it won't re-run when auth resolves.
        this.startPolling();
      }
    });
  }

  private handleDiffusionSession(): Subscription {
    return this.diffusionSessionService.diffusionSession$.subscribe((diffusionSession) => {
      if (diffusionSession) {
        // addStream must be registered BEFORE select() so the initial cached value delivered
        // by Diffusion on subscription is not missed. Everything that calls select() (pending
        // ISIN re-subscription and the isinsToSubscribe Subject) is therefore deferred until
        // after addStream is set up inside the import().then() callback. The diffusion module
        // is already loaded at this point so the .then() resolves in the next microtask —
        // well before any WebSocket message can arrive.
        void import('diffusion')
          .then((mod) => {
            // diffusion is a CJS module; esbuild may wrap exports under `default` in lazy chunks
            const datatypes = mod.datatypes ?? (mod as any).default?.datatypes;
            if (!datatypes) throw new Error('diffusion.datatypes export not found');
            if (!diffusionSession.isConnected()) return;
            this.valStream = diffusionSession.addStream('?wp//', datatypes.json());
            this.valStream?.on('value', this.onValueFromDiffusion.bind(this));
            this.valStream?.on('error', () => {
              if (diffusionSession?.isConnected()) {
                console.log('[PushQuote] Error in value stream');
              }
            });

            this.subscribeToDiffusionTaskSubscription = this.isinsToSubscribe.subscribe((isin) => {
              this.subscribeFromDiffusion(diffusionSession, isin);
            });

            this.unsubscribeFromDiffusionTaskSubscription = this.isinsToUnsubscribe.subscribe(
              (isin) => {
                this.unsubscribeFromDiffusion(diffusionSession, isin);
              },
            );

            const pendingIsins = Object.keys(this.isins);
            pendingIsins.forEach((isin) => this.subscribeFromDiffusion(diffusionSession, isin));

            if (this.pollingTimerSubscription) {
              this.pollingTimerSubscription.unsubscribe();
              this.pollingTimerSubscription = undefined;
            }
            if (this.subscribeToPollingTaskSubscription) {
              this.subscribeToPollingTaskSubscription.unsubscribe();
              this.subscribeToPollingTaskSubscription = undefined;
            }
          })
          .catch((err) => {
            console.error('[PushQuote] Failed to load diffusion module:', err);
            if (!this.pollingTimerSubscription && this.isLoggedIn) this.startPolling();
          });
      } else {
        // session lost — fall back to polling
        if (this.valStream) {
          this.valStream.close();
        }
        if (this.subscribeToDiffusionTaskSubscription) {
          this.subscribeToDiffusionTaskSubscription.unsubscribe();
          this.subscribeToDiffusionTaskSubscription = undefined;
        }
        if (this.unsubscribeFromDiffusionTaskSubscription) {
          this.unsubscribeFromDiffusionTaskSubscription.unsubscribe();
          this.unsubscribeFromDiffusionTaskSubscription = undefined;
        }
        if (!this.pollingTimerSubscription && this.isLoggedIn) {
          this.startPolling();
        }
      }
    });
  }

  private handleQuotePolling(): Subscription {
    return this.initialPollingTrigger$.pipe(debounceTime(100)).subscribe(() => {
      this.pollQuotes();
    });
  }

  private handlePushUnsubscribe(): Subscription {
    return timer(0, 5000).subscribe(() => {
      const isinsToUnsubscribe = Object.keys(this.isins).filter((isin) => {
        const subscriptionItem = this.isins[isin];
        const noObservers = !subscriptionItem.quoteSubject.observed;
        if (noObservers) {
          if (subscriptionItem.noObserversPresentTimestamp) {
            return (
              subscriptionItem.noObserversPresentTimestamp +
                this.deleteSubjectWithoutSubscriptAfter * 1000 <
              Date.now()
            );
          } else {
            subscriptionItem.noObserversPresentTimestamp = Date.now();
            return false;
          }
        } else {
          delete subscriptionItem.noObserversPresentTimestamp;
          return false;
        }
      });
      this.unsubscribeMulti(isinsToUnsubscribe);
    });
  }

  public subscribeMultiAccumulated(isins: string[]): Observable<Record<string, Quote>> {
    const accumulatedQuotes: Record<string, Quote> = {};

    return merge(...isins.map((isin) => this.subscribe(isin))).pipe(
      map((quote) => {
        if (quote?.isin) {
          accumulatedQuotes[quote.isin] = quote;
        }
        return { ...accumulatedQuotes };
      }),
    );
  }

  public subscribe(isin: string): Observable<Quote> {
    // Whatever it takes, poll the first quote even after navigation only
    this.initialPollingTrigger$.next();

    // eslint-disable-next-line no-prototype-builtins
    if (this.isins.hasOwnProperty(isin)) {
      // Book keeping
      delete this.isins[isin].noObserversPresentTimestamp;
      return this.isins[isin].quoteSubject;
    }

    // Book keeping
    const quoteSubject = new BehaviorSubject<any>(null);
    this.isins[isin] = { quoteSubject: quoteSubject };

    // Notify to subscribe isin
    this.isinsToSubscribe.next(isin);
    return quoteSubject.asObservable();
  }

  public unsubscribeMulti(isins: string[]): void {
    // eslint-disable-next-line @typescript-eslint/no-unused-expressions
    isins?.length && isins.forEach((isin) => this.unsubscribe(isin));
  }

  public unsubscribe(isin?: string): void {
    if (!isin) {
      return;
    }

    // eslint-disable-next-line no-prototype-builtins
    if (this.isins.hasOwnProperty(isin)) {
      // Book keeping
      this.isins[isin].quoteSubject.complete();
      delete this.isins[isin];

      // Notify to unsubscribe isin
      this.isinsToUnsubscribe.next(isin);
    }
  }

  protected onValueFromDiffusion(
    topic: string,
    _spec: TopicSpecification,
    newValue: unknown,
  ): void {
    const isin = topic.split('/')[1];

    // eslint-disable-next-line no-prototype-builtins
    if (this.isins.hasOwnProperty(isin)) {
      const newQuote = PushQuoteService.createQuoteFromDiffusionValue(newValue, isin);
      this.isins[isin].quoteSubject.next(newQuote);
    }
  }

  private subscribeFromDiffusion(diffusionSession: Session, isin: string): void {
    if (diffusionSession.isConnected()) {
      diffusionSession.select(`wp/${isin}`).then(
        () => {
          // subscribed successfully
        },
        (err) => {
          console.error('[PushQuote] Error subscribing to wp/' + isin + ':', err);
        },
      );
    }
  }

  private unsubscribeFromDiffusion(diffusionSession: Session, isin: string): void {
    if (diffusionSession.isConnected()) {
      diffusionSession.unsubscribe(`wp/${isin}`).then(
        () => {
          // nothing to do
        },
        () => {
          console.log('Error unsubscribing from diffusion topic');
        },
      );
    }
  }

  private startPolling(): void {
    this.pollingTimerSubscription = this.pollingTimer.subscribe(() => {
      this.pollQuotes();
    });
    this.subscribeToPollingTaskSubscription = this.isinsToSubscribe.subscribe(() => {
      // We don't poll quotes for every single ISIN!
      // this.pollQuote(isin);
    });
    // Poll immediately, then rely on the timer for subsequent polls
    this.pollQuotes();
  }

  private pollQuotes(): void {
    const isinsToPoll: string[] = [];
    Object.keys(this.isins).forEach((isin) => isinsToPoll.push(isin));
    // eslint-disable-next-line @typescript-eslint/no-unused-expressions
    isinsToPoll.length && this.doQuoteRequestViaPOST(isinsToPoll);
  }

  private doQuoteRequestViaPOST(isins: string[], host = this.activeApiHost): void {
    const token = this.authStore.token();
    if (!token || isins.length === 0) return;

    const url = `${host}/api/quote/?isins=${isins.join(',')}`;

    this.httpClient
      .get<Record<string, any>>(url, {
        withCredentials: true,
        headers: {
          'X-GB-Authorization': token,
        },
      })
      .subscribe({
        next: (quotesResult) => {
          this.consecutivePollingFailures = 0;

          if (host !== this.activeApiHost) {
            this.activeApiHost = host;
          }

          Object.entries(quotesResult).forEach(([resultIsin, quoteData]) => {
            const isinSubject = this.isins[resultIsin];
            if (isinSubject?.quoteSubject) {
              const quote = PushQuoteService.createQuoteFromJsonValue(quoteData, resultIsin);
              isinSubject.quoteSubject.next(quote);
            }
          });
        },
        error: (err) => {
          if (
            err.status === 401 &&
            environment.apihost_fallback &&
            host !== environment.apihost_fallback
          ) {
            this.doQuoteRequestViaPOST(isins, environment.apihost_fallback);
            return;
          }

          console.error(
            'Error polling quotes via GET',
            err.status,
            err.url,
            JSON.stringify(err.error),
          );

          this.consecutivePollingFailures++;

          if (this.consecutivePollingFailures >= this.maxPollingFailures) {
            console.error('Polling stopped after 3 consecutive failures');
            this.pollingTimerSubscription?.unsubscribe();
            this.subscribeToPollingTaskSubscription?.unsubscribe();
          }
        },
      });
  }
}
